// Formatting for the edge map hover and the source-of-edge stack (spec 2026-10-30 §6–§7).
import type { EdgePoint } from '../../api/client'
import { ANNUAL, type Horizon, type Measure, viewPoint, volPctYear } from './edgeMapGeometry'

const DASH = '—'
const YEAR_ROWS = 250

export function pctYear(bp: number | null | undefined): string {
  return bp == null || !Number.isFinite(bp) ? DASH : `${Math.round(bp * ANNUAL)} %/yr`
}

export function bpDay(bp: number | null | undefined): string {
  return bp == null || !Number.isFinite(bp) ? DASH : `${bp.toFixed(1)} bp/d`
}

const ROWS: { label: string; get: (p: EdgePoint) => number }[] = [
  { label: 'stale-pricing return', get: p => p.staleBp },
  { label: 'continuation (momentum)', get: p => p.continuationBp },
  { label: 'buy-and-hold drift', get: p => p.driftBp },
  { label: 'unexplained', get: p => p.unexplainedBp },
  { label: 'rule payoff, total', get: p => p.payoffBp },
  { label: 'buy & hold', get: p => p.bhBp },
]

function pctPlain(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? DASH : `${Math.round(v)} %/yr`
}

function ratio(v: number | null | undefined): string {
  return v == null || !Number.isFinite(v) ? DASH : v.toFixed(2)
}

/** Hover rows: the six sources as % per year with bp/day in brackets, then NAV volatility and the rule's CAGR, each
 *  with the chosen horizon's value when it exists. In the 'risk' view two more rows give stale pricing and the payoff
 *  per unit of risk (annual return / annual volatility). */
export function hoverRows(now: EdgePoint, ago: EdgePoint | null, horizon: Horizon,
  measure: Measure = 'return'): { label: string; value: string }[] {
  const was = (s: string) => (ago ? ` [${horizon} ago ${s}]` : '')
  const rows = ROWS.map(r => ({
    label: r.label,
    value: `${pctYear(r.get(now))} (${bpDay(r.get(now))})` + was(pctYear(ago ? r.get(ago) : null)),
  }))
  rows.push({ label: 'NAV volatility', value: pctPlain(volPctYear(now)) + was(pctPlain(ago ? volPctYear(ago) : null)) })
  rows.push({ label: 'rule CAGR', value: pctPlain(now.ruleCagrPct) + was(pctPlain(ago?.ruleCagrPct)) })
  if (measure === 'risk') {
    const v = viewPoint(now, 'risk'), a = ago ? viewPoint(ago, 'risk') : null
    rows.push({ label: 'stale pricing per unit of risk', value: ratio(v?.staleBp) + was(ratio(a?.staleBp)) })
    rows.push({ label: 'payoff per unit of risk', value: ratio(v?.payoffBp) + was(ratio(a?.payoffBp)) })
  }
  return rows
}

/** "stale pricing X % of payoff, was Y % a year ago", from the last point and the one 250 rows earlier.
 *  Empty when the series is shorter than a year or either payoff is not positive. */
export function readingLine(series: EdgePoint[]): string {
  if (series.length <= YEAR_ROWS) return ''
  const now = series[series.length - 1]
  const ago = series[series.length - 1 - YEAR_ROWS]
  if (!(now.payoffBp > 0) || !(ago.payoffBp > 0)) return ''
  const share = (p: EdgePoint) => Math.round(100 * p.staleBp / p.payoffBp)
  return `stale pricing ${share(now)} % of payoff, was ${share(ago)} % a year ago`
}
